import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Confetti from "react-confetti";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { get, isEmpty, replace } from "lodash";

import { navigateTo } from "../../utils/location";
import { loadGifts, markGiftSeen } from "../../store/user/actions";
import { getUserFirstName } from "../../logic/user/getters/getUserFirstName";
import GiftIcon from "../icons/Gift.svg";

const AUTO_DISMISS_MS = 8000;
const EXIT_ANIMATION_MS = 320;
const CONFETTI_DURATION_MS = 5000;
const MARK_SEEN_DELAY_MS = 400;

const SIMULATE_QUERY_PARAM = "simulateNewReward";
const SIMULATED_GIFT_ID = "__simulated_new_reward__";
const CELEBRATED_STORAGE_KEY = "newRewardToast.celebratedIds";

// Only celebrate rewards earned on or after this date (frozen: 7 days before the el-al-rewards-widget launch on 2026-04-20).
const EARNED_SINCE_DATE = "2026-04-13";
const EARNED_SINCE_CUTOFF_MS = new Date(`${EARNED_SINCE_DATE}T00:00:00Z`).getTime();

const isSimulateModeActive = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return new URLSearchParams(window.location.search).has(SIMULATE_QUERY_PARAM);
};

const buildSimulatedGift = (T) => ({
  couponInstance: {
    id: SIMULATED_GIFT_ID,
    firstSeenDate: null,
    createdAt: new Date().toISOString(),
    coupon: {
      title: T("Simulated Reward \u2014 Free Coffee"),
    },
  },
  couponImagePath: null,
});

const readCelebratedIds = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return new Set();
  }
  try {
    const raw = window.sessionStorage.getItem(CELEBRATED_STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (err) {
    return new Set();
  }
};

const rememberCelebratedId = (id) => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    const current = readCelebratedIds();
    current.add(id);
    window.sessionStorage.setItem(
      CELEBRATED_STORAGE_KEY,
      JSON.stringify(Array.from(current)),
    );
  } catch (err) {
    /* sessionStorage unavailable — ignore */
  }
};

const NewRewardToast = ({ appStyles = {}, T = (s) => s }) => {
  const dispatch = useDispatch();
  const { loggedIn, gifts, loadingGifts, firstName } = useSelector((state) => ({
    loggedIn: state.user.loggedIn,
    gifts: state.user.gifts,
    loadingGifts: state.user.loadingGifts,
    firstName: (getUserFirstName(state.user) || "").trim(),
  }));

  const simulate = isSimulateModeActive();

  const [celebratedInSession] = useState(readCelebratedIds);
  const celebratedRef = useRef(null);
  const loadGiftsDispatchedRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (simulate || loadGiftsDispatchedRef.current) {
      return;
    }
    if (!loggedIn || loadingGifts) {
      return;
    }
    loadGiftsDispatchedRef.current = true;
    if (isEmpty(gifts)) {
      dispatch(loadGifts());
    }
  }, [simulate, loggedIn, loadingGifts, gifts, dispatch]);

  const activeGift = useMemo(() => {
    if (simulate) {
      return buildSimulatedGift(T);
    }
    if (!loggedIn || !gifts) return null;
    return gifts.find((g) => {
      if (!g.couponInstance) return false;
      if (!g.couponInstance.id) return false;
      if (g.couponInstance.firstSeenDate) return false;
      if (celebratedInSession.has(g.couponInstance.id)) return false;
      const createdAtMs = new Date(g.couponInstance.createdAt).getTime();
      if (!createdAtMs || createdAtMs < EARNED_SINCE_CUTOFF_MS) return false;
      return true;
    });
  }, [simulate, loggedIn, gifts, celebratedInSession, T]);

  const activeGiftId = activeGift && activeGift.couponInstance.id;

  useEffect(() => {
    if (!activeGiftId || activeGiftId === celebratedRef.current) {
      return undefined;
    }

    celebratedRef.current = activeGiftId;
    setVisible(true);
    setExiting(false);
    setShowConfetti(true);

    if (!simulate) {
      rememberCelebratedId(activeGiftId);
    }

    const markSeenTimer = !simulate
      ? setTimeout(() => {
          dispatch(markGiftSeen(activeGiftId));
        }, MARK_SEEN_DELAY_MS)
      : null;

    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, CONFETTI_DURATION_MS);

    const exitStartTimer = setTimeout(() => {
      setExiting(true);
    }, AUTO_DISMISS_MS);

    const unmountTimer = setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, AUTO_DISMISS_MS + EXIT_ANIMATION_MS);

    return () => {
      if (markSeenTimer) clearTimeout(markSeenTimer);
      clearTimeout(confettiTimer);
      clearTimeout(exitStartTimer);
      clearTimeout(unmountTimer);
    };
  }, [activeGiftId, dispatch, simulate]);

  if (!visible || !activeGift || typeof document === "undefined") {
    return null;
  }

  const coupon = get(activeGift, "couponInstance.coupon", {});
  const imageUrl = get(activeGift, "couponImagePath.url")
    ? replace(activeGift.couponImagePath.url, /http:\/\//g, "https://")
    : null;

  const accent = appStyles.accentColor || "#ff3366";
  const action = appStyles.actionColor || "#222";
  const isRtl = Boolean(appStyles.rtl);
  const TapArrow = isRtl ? ChevronLeft : ChevronRight;

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setExiting(true);
    setShowConfetti(false);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, EXIT_ANIMATION_MS);
  };

  const handleClick = () => {
    navigateTo("/gifts");
    handleClose();
  };

  const overlay = (
    <>
      {showConfetti && (
        <Confetti
          width={typeof window !== "undefined" ? window.innerWidth : 0}
          height={typeof window !== "undefined" ? window.innerHeight : 0}
          colors={[accent, action, "#fff", "#ffd166"]}
          numberOfPieces={320}
          recycle={false}
          initialVelocityX={5}
          initialVelocityY={6}
          gravity={0.25}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            pointerEvents: "none",
            zIndex: 10000,
          }}
        />
      )}
      <Box
        role="alert"
        aria-live="polite"
        onClick={handleClick}
        sx={{
          position: "fixed",
          top: "max(16px, env(safe-area-inset-top))",
          left: "50%",
          zIndex: 10001,
          width: "calc(100vw - 24px)",
          maxWidth: 440,
          cursor: "pointer",
          borderRadius: "18px",
          overflow: "hidden",
          direction: isRtl ? "rtl" : "ltr",
          background: `linear-gradient(135deg, ${accent} 0%, ${action} 100%)`,
          boxShadow: `0 14px 40px ${accent}55, 0 4px 12px rgba(0,0,0,0.18)`,
          transformOrigin: "top center",
          transform: exiting
            ? "translate(-50%, -160%) scale(0.96)"
            : "translate(-50%, 0) scale(1)",
          opacity: exiting ? 0 : 1,
          transition: `transform ${EXIT_ANIMATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${EXIT_ANIMATION_MS}ms ease`,
          animation: exiting
            ? "none"
            : "newRewardToastIn 480ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          "@keyframes newRewardToastIn": {
            "0%": {
              transform: "translate(-50%, -160%) scale(0.9)",
              opacity: 0,
            },
            "100%": {
              transform: "translate(-50%, 0) scale(1)",
              opacity: 1,
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at 15% 20%, rgba(255,255,255,0.35), transparent 55%)",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "14px 12px 14px 14px",
            gap: "14px",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              flexShrink: 0,
              borderRadius: "14px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.18)",
              border: "2px solid rgba(255,255,255,0.55)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <GiftIcon
                style={{ width: 34, height: 34, fill: "#fff", color: "#fff" }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="div"
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: "17px",
                lineHeight: 1.2,
                textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span role="img" aria-label="sparkles">
                ✨
              </span>
              {loggedIn && firstName
                ? `${T("Hi")} ${firstName}, ${T("new reward...")}`
                : T("New reward...")}
            </Typography>
            <Typography
              component="div"
              sx={{
                marginTop: "6px",
                color: "rgba(255,255,255,0.95)",
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {coupon.title || T("A new reward is waiting for you!")}
            </Typography>
            <Typography
              component="div"
              sx={{
                marginTop: "6px",
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "0.3px",
                padding: "3px 10px 3px 12px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.22)",
                border: "1px solid rgba(255,255,255,0.4)",
                animation: "newRewardTapPulse 1600ms ease-in-out infinite",
                "@keyframes newRewardTapPulse": {
                  "0%, 100%": { transform: "translateX(0)" },
                  "50%": {
                    transform: isRtl ? "translateX(-3px)" : "translateX(3px)",
                  },
                },
              }}
            >
              {T("Tap to view")}
              <TapArrow sx={{ fontSize: 18, marginLeft: isRtl ? 0 : "-2px", marginRight: isRtl ? "-2px" : 0 }} />
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            aria-label={T("Close")}
            size="small"
            sx={{
              flexShrink: 0,
              color: "#fff",
              background: "rgba(255,255,255,0.18)",
              border: "1.5px solid rgba(255,255,255,0.4)",
              width: 32,
              height: 32,
              "&:hover": {
                background: "rgba(255,255,255,0.3)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 3,
            background: "rgba(255,255,255,0.85)",
            borderRadius: "0 3px 0 0",
            animation: exiting
              ? "none"
              : `newRewardToastProgress ${AUTO_DISMISS_MS}ms linear forwards`,
            "@keyframes newRewardToastProgress": {
              "0%": { width: "100%" },
              "100%": { width: "0%" },
            },
          }}
        />
      </Box>
    </>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default NewRewardToast;
